// BOOK WITHOUT PAYING, CARD BY LINK, AND WHETHER IT IS ON.
//
// PAY_BY_LINK (docs/DECISIONS.md, 1 Nov 2026; app/services/order/pay_by_link.rb)
// takes the card ask off the booking screen. Submitting creates a booking
// waiting for its card, and a link to the same hosted Stripe Checkout the card
// path uses is sent, WhatsApp first and email as the backup. Adding the card is
// what makes it an order.
//
// IT TAKES NO MONEY. Robin, 1 Nov 2026: "charge should still be when order is
// accepted rather than link settled, so its alinged with current point of
// payment in alt A". The link saves a card, exactly as the form does today,
// and the charge happens at the same point as on every other booking. So every
// sentence about charging here comes from lib/payment-copy.js, which reads
// CHARGE_ON_COMPLETION: one flip moves the behaviour and the words together.
//
// THE FORM SAYS NOTHING ABOUT PAYMENT. Robin, same day: "when user hits the
// book without pay form I dont think its an option I think we either say
// nothing or that we will share payment link, Im thinking nothing as it doesnt
// block anything, we then send the link and if someone complains and says Id
// rather pay on the say after wash, we know.." So there is no card field, no
// "we will send you a link", no helper line and no asterisk: announcing the
// arrangement would measure people's reaction to the arrangement instead of
// the effect of dropping the card ask, and silence blocks nothing.
//
// The one action is "Book now". Robin, same day: "but its not book now, pay to
// confirm, its just book now lol". Nothing appended.
//
// The SUCCESS state is a different surface and is plain about what happens
// next (step-finish), because silence there would create a false impression
// rather than withhold a frame.
//
// The server writes the switch into the `gwmc:pay-by-link` meta tag only while
// it is on (app/views/layouts/gwmc.html.haml); no tag is off, which is what
// production does until Robin flips it. Never a build-time constant.

import { payLead, payDetail } from './payment-copy.js'

const readMode = () => {
  if (typeof document === 'undefined') return null
  const tag = document.querySelector('meta[name="gwmc:pay-by-link"]')
  return tag ? tag.getAttribute('content') : null
}

export const payByLink = () => readMode() === 'on'

// The primary action, and the whole of it.
export const BOOK_LABEL = 'Book now'

// What the screen says once the booking is in. Plain, and never a
// confirmation: we have their details, the CARD confirms the wash, and no
// slot is held (operator availability is still a stub).
export const PENDING_HEADING = 'We have your details'

// Which channel actually carried the link, from the server's answer
// (`channel`: whatsapp | whatsapp_template | email | none). Saying "emailed"
// when it went to WhatsApp would be the same kind of small lie this whole
// screen is written to avoid.
export const pendingLead = ({ channel, email } = {}) => {
  if (channel === 'whatsapp' || channel === 'whatsapp_template') {
    return 'We have sent a link to your WhatsApp. Adding your card there confirms your wash.'
  }
  if (channel === 'none') {
    return 'We have your booking. We will be in touch with a link to add your card, which confirms your wash.'
  }
  return email
    ? `We have emailed a link to ${email}. Adding your card there confirms your wash.`
    : 'We have emailed you a link to add your card, which confirms your wash.'
}

export const PENDING_DETAIL =
  'Your wash is not confirmed with an operator until your card is on the booking, and the day and window ' +
  'you chose are the ones we will aim for, not a held slot.'

// When the card is charged, in the words arm A uses, read off the same
// switch. Nothing is taken at the link.
export const pendingChargeLine = () => `Nothing is taken when you add it. ${payLead()} ${payDetail()}`

// The page's own light reading of a typed email, so the link has a backup
// channel. It NEVER refuses the booking: an address we cannot read is simply
// not sent, the order reports a missing email and Aider picks it up
// (Order#missing_details). Same shape as the card path's check
// (lib/payer-email.js) and deliberately no stricter.
export const readEmail = value => {
  const typed = String(value || '').trim().toLowerCase()
  return /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/.test(typed) ? typed : null
}
